import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Code,
  CopyButton,
  Group,
  Menu,
  Modal,
  SegmentedControl,
  Skeleton,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconArchive,
  IconCheck,
  IconCirclePlus,
  IconCopy,
  IconDotsVertical,
  IconLogin2,
  IconRestore,
} from '@tabler/icons-react';
import { useState } from 'react';
import { motion } from 'framer-motion';
import { notifications } from '@mantine/notifications';
import { trpc } from '../../lib/trpc';

type StatusFilter = 'all' | 'Active' | 'Archived';

function VendorsPage() {
  const utils = trpc.useUtils();
  const navigate = useNavigate();
  const [filter, setFilter] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [opened, { open, close }] = useDisclosure(false);
  const [credModalOpen, credModalH] = useDisclosure(false);
  const [generatedCreds, setGeneratedCreds] = useState<{
    email: string;
    password: string;
  } | null>(null);

  const list = trpc.tenancy.vendors.list.useQuery({
    search: filter || undefined,
    status: status === 'all' ? undefined : status,
    take: 100,
  });

  const create = trpc.tenancy.vendors.create.useMutation({
    onSuccess: (data) => {
      void utils.tenancy.vendors.list.invalidate();
      close();
      if (data.admin?.password) {
        setGeneratedCreds({ email: data.admin.email, password: data.admin.password });
        credModalH.open();
      } else {
        notifications.show({ message: 'Vendor created', color: 'green' });
      }
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  const archive = trpc.tenancy.vendors.archive.useMutation({
    onSuccess: () => {
      void utils.tenancy.vendors.list.invalidate();
      notifications.show({ message: 'Vendor archived', color: 'orange' });
    },
  });
  const unarchive = trpc.tenancy.vendors.unarchive.useMutation({
    onSuccess: () => {
      void utils.tenancy.vendors.list.invalidate();
      notifications.show({ message: 'Vendor reactivated', color: 'green' });
    },
  });
  const loginAs = trpc.auth.startImpersonationByVendor.useMutation({
    onSuccess: async () => {
      // Refresh /auth/me so navbar reflects the new (vendor) session, then
      // hop into the vendor dashboard.
      await utils.auth.me.invalidate();
      navigate({ to: '/vendor' });
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Stack gap="md">
        <Group justify="space-between">
          <Title order={2}>Vendors</Title>
          <Button leftSection={<IconCirclePlus size={16} />} onClick={open}>
            New vendor
          </Button>
        </Group>

        <Group>
          <TextInput
            placeholder="Search title or slug…"
            value={filter}
            onChange={(e) => setFilter(e.currentTarget.value)}
            style={{ flex: 1 }}
          />
          <SegmentedControl
            value={status}
            onChange={(v) => setStatus(v as StatusFilter)}
            data={[
              { value: 'all', label: 'All' },
              { value: 'Active', label: 'Active' },
              { value: 'Archived', label: 'Archived' },
            ]}
          />
        </Group>

        <Card p={0}>
          {list.isPending ? (
            <Stack p="md" gap="sm">
              <Skeleton height={28} />
              <Skeleton height={28} />
              <Skeleton height={28} />
            </Stack>
          ) : list.isError ? (
            <Text p="md" c="red">
              {list.error.message}
            </Text>
          ) : (
            <Table striped highlightOnHover withRowBorders={false}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Vendor</Table.Th>
                  <Table.Th>Admin</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Created</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {(list.data?.vendors ?? []).map((v) => (
                  <Table.Tr key={v.id}>
                    <Table.Td>
                      <Anchor
                        renderRoot={(props) => (
                          <Link to="/central/vendors/$uid" params={{ uid: v.uid }} {...props} />
                        )}
                      >
                        <Text fw={500}>{v.title}</Text>
                      </Anchor>
                      <Text size="xs" c="dimmed" ff="monospace">
                        {v.slug}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {v.admin ? (
                        <Stack gap={0}>
                          <Text size="sm">
                            {v.admin.displayName ||
                              [v.admin.firstName, v.admin.lastName].filter(Boolean).join(' ') ||
                              '—'}
                          </Text>
                          <Text size="xs" c="dimmed">
                            {v.admin.email}
                          </Text>
                        </Stack>
                      ) : (
                        <Text size="xs" c="dimmed">
                          No admin
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Badge color={v.status === 'Active' ? 'green' : 'gray'} variant="light">
                        {v.status}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">
                        {new Date(v.createdAt).toLocaleDateString()}
                      </Text>
                    </Table.Td>
                    <Table.Td align="right">
                      <Menu shadow="md" position="bottom-end">
                        <Menu.Target>
                          <ActionIcon variant="subtle">
                            <IconDotsVertical size={14} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Item
                            leftSection={<IconLogin2 size={14} />}
                            disabled={!v.admin || v.status !== 'Active'}
                            onClick={() => loginAs.mutate({ vendorId: v.id })}
                          >
                            Login as Vendor Admin
                          </Menu.Item>
                          <Menu.Divider />
                          {v.status === 'Active' ? (
                            <Menu.Item
                              leftSection={<IconArchive size={14} />}
                              color="orange"
                              onClick={() => archive.mutate({ vendorId: v.id })}
                            >
                              Archive
                            </Menu.Item>
                          ) : (
                            <Menu.Item
                              leftSection={<IconRestore size={14} />}
                              color="blue"
                              onClick={() => unarchive.mutate({ vendorId: v.id })}
                            >
                              Reactivate
                            </Menu.Item>
                          )}
                        </Menu.Dropdown>
                      </Menu>
                    </Table.Td>
                  </Table.Tr>
                ))}
                {(list.data?.vendors ?? []).length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={5}>
                      <Text c="dimmed" ta="center" py="md">
                        No vendors{' '}
                        {filter || status !== 'all' ? 'match the current filters' : 'yet'}.
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          )}
        </Card>
      </Stack>

      <CreateVendorModal
        opened={opened}
        onClose={close}
        onSubmit={(v) => create.mutate(v)}
        loading={create.isPending}
      />

      {/* Generated credentials modal — shown once after create */}
      <Modal
        opened={credModalOpen}
        onClose={() => {
          credModalH.close();
          setGeneratedCreds(null);
          notifications.show({ message: 'Vendor created', color: 'green' });
        }}
        title="Vendor admin credentials"
        centered
        closeOnClickOutside={false}
      >
        <Stack>
          <Alert color="orange">
            Copy this password now — it won&apos;t be shown again. Share it with the vendor admin
            via your normal out-of-band channel.
          </Alert>
          <Group>
            <Text size="sm" c="dimmed" w={80}>
              Email
            </Text>
            <Code style={{ flex: 1 }}>{generatedCreds?.email}</Code>
          </Group>
          <Group>
            <Text size="sm" c="dimmed" w={80}>
              Password
            </Text>
            <Code style={{ flex: 1 }}>{generatedCreds?.password}</Code>
            <CopyButton value={generatedCreds?.password ?? ''} timeout={2000}>
              {({ copied, copy }) => (
                <Tooltip label={copied ? 'Copied' : 'Copy password'}>
                  <ActionIcon variant="subtle" onClick={copy} color={copied ? 'green' : 'gray'}>
                    {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                  </ActionIcon>
                </Tooltip>
              )}
            </CopyButton>
          </Group>
          <Group justify="end">
            <Button
              onClick={() => {
                credModalH.close();
                setGeneratedCreds(null);
              }}
            >
              I&apos;ve saved the password
            </Button>
          </Group>
        </Stack>
      </Modal>
    </motion.div>
  );
}

// ── Create modal ──────────────────────────────────────────────────────

function CreateVendorModal({
  opened,
  onClose,
  onSubmit,
  loading,
}: {
  opened: boolean;
  onClose: () => void;
  onSubmit: (input: {
    title: string;
    slug: string;
    admin?: {
      email: string;
      firstName: string;
      lastName?: string;
      mobile?: string;
      password?: string;
    };
  }) => void;
  loading: boolean;
}) {
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [autoSlug, setAutoSlug] = useState(true);
  const [adminEmail, setAdminEmail] = useState('');
  const [adminFirstName, setAdminFirstName] = useState('');
  const [adminLastName, setAdminLastName] = useState('');
  const [adminMobile, setAdminMobile] = useState('');
  const [autoPassword, setAutoPassword] = useState(true);
  const [customPassword, setCustomPassword] = useState('');

  const slugify = (s: string) =>
    s
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60);

  const submit = () => {
    onSubmit({
      title: title.trim(),
      slug: autoSlug ? slugify(title) : slug.trim(),
      admin: adminEmail
        ? {
            email: adminEmail.trim(),
            firstName: adminFirstName.trim() || 'Admin',
            ...(adminLastName.trim() ? { lastName: adminLastName.trim() } : {}),
            ...(adminMobile.trim() ? { mobile: adminMobile.trim() } : {}),
            ...(!autoPassword && customPassword ? { password: customPassword } : {}),
          }
        : undefined,
    });
  };

  const reset = () => {
    setTitle('');
    setSlug('');
    setAutoSlug(true);
    setAdminEmail('');
    setAdminFirstName('');
    setAdminLastName('');
    setAdminMobile('');
    setAutoPassword(true);
    setCustomPassword('');
  };

  return (
    <Modal
      opened={opened}
      onClose={() => {
        onClose();
        reset();
      }}
      title="Create vendor"
      centered
      size="lg"
    >
      <Stack>
        <Alert color="blue" variant="light">
          We&apos;ll atomically create the vendor + the admin user in one step. The admin gets a
          generated password that&apos;s shown once for you to share.
        </Alert>

        <TextInput
          label="Business name"
          required
          value={title}
          onChange={(e) => {
            setTitle(e.currentTarget.value);
            if (autoSlug) setSlug(slugify(e.currentTarget.value));
          }}
        />
        <Group grow>
          <TextInput
            label="Slug"
            description="Lowercase + dashes only — used in URLs and webhooks"
            required
            value={autoSlug ? slugify(title) : slug}
            onChange={(e) => setSlug(e.currentTarget.value)}
            disabled={autoSlug}
          />
          <Switch
            label="Auto-generate from title"
            checked={autoSlug}
            onChange={(e) => setAutoSlug(e.currentTarget.checked)}
            mt={24}
          />
        </Group>

        <Title order={5} mt="sm">
          Admin user
        </Title>
        <TextInput
          label="Admin email"
          required
          type="email"
          value={adminEmail}
          onChange={(e) => setAdminEmail(e.currentTarget.value)}
        />
        <Group grow>
          <TextInput
            label="First name"
            required
            value={adminFirstName}
            onChange={(e) => setAdminFirstName(e.currentTarget.value)}
          />
          <TextInput
            label="Last name"
            value={adminLastName}
            onChange={(e) => setAdminLastName(e.currentTarget.value)}
          />
        </Group>
        <TextInput
          label="Mobile (optional)"
          value={adminMobile}
          onChange={(e) => setAdminMobile(e.currentTarget.value)}
        />

        <Switch
          label="Auto-generate password (recommended)"
          description="A 12-character random password will be generated and shown once."
          checked={autoPassword}
          onChange={(e) => setAutoPassword(e.currentTarget.checked)}
        />
        {!autoPassword && (
          <TextInput
            label="Custom password (min 8 chars)"
            type="text"
            value={customPassword}
            onChange={(e) => setCustomPassword(e.currentTarget.value)}
          />
        )}

        <Group justify="end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={loading}
            disabled={
              !title.trim() ||
              !adminEmail.trim() ||
              !adminFirstName.trim() ||
              (!autoPassword && customPassword.length < 8)
            }
            onClick={submit}
          >
            Create vendor
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export const Route = createFileRoute('/central/vendors')({ component: VendorsPage });
